import {
  Flag,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Target,
} from 'lucide-react';
import React from 'react';
import { useGetDeadlinesQuery } from '../../services/dashboardApi';
import { WidgetProps } from './types';
import { WidgetLoadingSkeleton, WidgetEmptyState } from './WidgetErrorBoundary';

const MilestonesWidget: React.FC<WidgetProps> = ({ scope, projectId, size }) => {
  const { data, isLoading, error } = useGetDeadlinesQuery(
    {
      projectId: scope === 'project' ? projectId : undefined,
      limit: size === '2x2' ? 8 : size === '2x1' ? 5 : 3,
    },
    { pollingInterval: 300000 }
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDaysUntil = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'on_track': {
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      }
      case 'at_risk': {
        return <Clock className="h-4 w-4 text-yellow-600" />;
      }
      case 'overdue': {
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      }
      default: {
        return <Flag className="h-4 w-4 text-gray-600" />;
      }
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high': {
        return 'bg-red-100 text-red-700';
      }
      case 'medium': {
        return 'bg-yellow-100 text-yellow-700';
      }
      case 'low': {
        return 'bg-green-100 text-green-700';
      }
      default: {
        return 'bg-gray-100 text-gray-700';
      }
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'milestone': {
        return <Flag className="h-3 w-3" />;
      }
      case 'task': {
        return <CheckCircle2 className="h-3 w-3" />;
      }
      case 'deliverable': {
        return <Target className="h-3 w-3" />;
      }
      case 'contract': {
        return <Calendar className="h-3 w-3" />;
      }
      default: {
        return <Flag className="h-3 w-3" />;
      }
    }
  };

  if (isLoading) {
    return <WidgetLoadingSkeleton lines={4} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-600 text-sm font-medium">Failed to load milestones</p>
          <p className="text-gray-500 text-xs mt-1">Please try refreshing</p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <WidgetEmptyState
        title="No Upcoming Deadlines"
        description="All caught up! No milestones due soon"
        icon={Flag}
      />
    );
  }

  const isCompact = size === '1x1';

  // Group by status for summary
  const statusCounts = data.reduce(
    (acc, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <div className="space-y-3">
      {/* Status Summary (for larger sizes) */}
      {!isCompact && (
        <div className="grid grid-cols-3 gap-2 pb-2 border-b border-gray-200">
          <div className="text-center">
            <p className="text-lg font-bold text-green-600">{statusCounts['on_track'] || 0}</p>
            <p className="text-xs text-gray-500">On Track</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-yellow-600">{statusCounts['at_risk'] || 0}</p>
            <p className="text-xs text-gray-500">At Risk</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-red-600">{statusCounts['overdue'] || 0}</p>
            <p className="text-xs text-gray-500">Overdue</p>
          </div>
        </div>
      )}

      {/* Deadlines List */}
      <div className="space-y-2">
        {data.map(deadline => {
          const daysUntil = getDaysUntil(deadline.dueDate);

          return (
            <div
              key={deadline.id}
              className={`rounded-lg p-3 border transition-colors hover:shadow-sm cursor-pointer ${
                deadline.status === 'overdue'
                  ? 'border-red-200 bg-red-50/50'
                  : deadline.status === 'at_risk'
                    ? 'border-yellow-200 bg-yellow-50/50'
                    : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-2 flex-1 min-w-0">
                  {getStatusIcon(deadline.status)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {deadline.title}
                      </h4>
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded ${getPriorityBadge(deadline.priority)}`}
                      >
                        {deadline.priority}
                      </span>
                    </div>

                    <div className="flex items-center space-x-3 mt-1">
                      {/* Type */}
                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                        {getTypeIcon(deadline.type)}
                        <span className="capitalize">{deadline.type}</span>
                      </div>

                      {/* Due Date */}
                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(deadline.dueDate)}</span>
                      </div>
                    </div>

                    {/* Project (for org scope) */}
                    {scope === 'org' && deadline.project && !isCompact && (
                      <p className="text-xs text-blue-600 mt-1 truncate">{deadline.project.name}</p>
                    )}

                    {/* Assignee */}
                    {!isCompact && deadline.assignee && (
                      <p className="text-xs text-gray-500 mt-1">
                        Assigned to: {deadline.assignee.name}
                      </p>
                    )}
                  </div>
                </div>

                {/* Days Until */}
                <div className="flex-shrink-0 ml-2 text-right">
                  <div
                    className={`text-xs px-2 py-1 rounded ${
                      daysUntil < 0
                        ? 'bg-red-100 text-red-700'
                        : daysUntil <= 3
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {daysUntil < 0
                      ? `${Math.abs(daysUntil)}d overdue`
                      : daysUntil === 0
                        ? 'Today'
                        : daysUntil === 1
                          ? 'Tomorrow'
                          : `${daysUntil}d left`}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* View All Link */}
      {size === '2x2' && (
        <div className="pt-2 border-t border-gray-200">
          <button className="w-full flex items-center justify-center space-x-1 text-xs text-blue-600 hover:text-blue-700 font-medium py-1">
            <span>View All Milestones</span>
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MilestonesWidget;
